import { useParams } from 'react-router-dom';
import {
  useDisclosure,
  Alert,
  AlertIcon,
  Button,
  Skeleton,
  Spinner,
} from '@chakra-ui/react';
import { FiArrowLeft } from 'react-icons/fi';
import { ProjectHeader } from '../../components/v0/project/ProjectHeader';
import { ProjectInfo } from '../../components/v0/project/ProjectInfo';
import { ProjectTaskList } from '../../components/v0/project/ProjectTaskList';
import { TaskCreateModal } from '../../components/v0/project/TaskCreateModal';
import { ProjectDeleteModal } from '../../components/v0/project/ProjectDeleteModal';
import { ProjectShareModal } from '../../components/v0/project/ProjectShareModal';
import { useProjectDetail } from '../../hooks/v0/useProjectDetail';
import { CreateTaskData } from '../../types/task';
import { useTranslation } from 'react-i18next';
import { useTransition } from 'react';

export const ProjectDetail = () => {
  const { id } = useParams<{ id: string }>();
  const taskModalDisclosure = useDisclosure();
  const confirmDeleteDisclosure = useDisclosure();
  const shareModalDisclosure = useDisclosure();
  const { t } = useTranslation(['v0/projects', 'v0/common']);
  const [isPending, startTransition] = useTransition();

  // 使用自定义钩子加载项目和任务数据，以及处理相关操作
  const {
    project,
    projectTasks,
    loading,
    tasksLoading,
    isEditing,
        title,
        context,
    isBackgroundRefreshing,
    tasksTotal,
    tasksVital,
    tasksUrgent,
    formatDate,
    handleSave,
    handleDelete,
    handleCreateTask,
    handleToggleTaskDone,
    handleDeleteTask,
    handleSelectTask,
    handleTitleChange,
    handleContextChange,
    toggleEditMode,
    cancelEdit,
    navigateToProjects
  } = useProjectDetail(id);

  // 处理创建任务，确保返回布尔值，并使用 startTransition 包装
  const handleCreateTaskWrapper = async (data: CreateTaskData): Promise<boolean> => {
    let result: boolean | undefined;

    // 使用 startTransition 包装异步操作，避免 Suspense 错误
    await new Promise<void>((resolve) => {
      startTransition(async () => {
        result = await handleCreateTask(data);
        resolve();
      });
    });

    // 确保返回布尔值
    return result === true;
  };

  if (loading && !project) {
    return (
      <div className={`min-h-screen pt-4 md:pt-6 bg-gray-50 dark:bg-gray-900`}>
        <div className="container mx-auto py-8 px-3 md:px-6 max-w-4xl">
          <Skeleton height="40px" width="200px" mb={4} />
          <Skeleton height="20px" mb={2} />
          <Skeleton height="20px" mb={2} />
          <Skeleton height="20px" mb={4} />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
            <Skeleton height="100px" borderRadius="lg" />
            <Skeleton height="100px" borderRadius="lg" />
            <Skeleton height="100px" borderRadius="lg" />
          </div>

          <Skeleton height="30px" width="150px" mb={4} />
          <Skeleton height="80px" mb={3} borderRadius="lg" />
          <Skeleton height="80px" mb={3} borderRadius="lg" />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen pt-4 md:pt-6 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto py-8 px-3 md:px-6 max-w-4xl">
          <Alert status="error" mb={4} borderRadius="lg">
            <AlertIcon />
            {t('v0/projects:errors.projectNotFound')}
          </Alert>
          <Button leftIcon={<FiArrowLeft />} onClick={navigateToProjects}>
            {t('v0/projects:actions.backToList')}
          </Button>
        </div>
      </div>
    );
  }

  if (project && projectTasks) {
    return (
      <div className="min-h-screen pt-4 md:pt-6 pb-20 md:pb-10 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-3 md:px-6 max-w-4xl">
          {/* 项目头部 */}
          <ProjectHeader
            isEditing={isEditing}
            onSave={() => {
              // 使用 startTransition 包装可能导致 Suspense 的操作
              startTransition(() => {
                handleSave();
              });
            }}
            onCancel={cancelEdit}
            onToggleEdit={() => {
              // 使用 startTransition 包装状态更新
              startTransition(() => {
                toggleEditMode();
              });
            }}
            onDelete={confirmDeleteDisclosure.onOpen}
            onShare={shareModalDisclosure.onOpen}
            navigateBack={() => {
              // 使用 startTransition 包装导航操作
              startTransition(() => {
                navigateToProjects();
              });
            }}
          />

          {/* 项目信息 */}
          <ProjectInfo
            project={project}
            isEditing={isEditing}
            title={title}
            context={context}
            tasksTotal={tasksTotal}
            tasksVital={tasksVital}
            tasksUrgent={tasksUrgent}
            formatDate={formatDate}
            onTitleChange={handleTitleChange}
            onContextChange={handleContextChange}
          />

          {/* 项目任务列表 */}
          <ProjectTaskList
            tasks={projectTasks}
            loading={tasksLoading || isPending}
            isBackgroundRefreshing={isBackgroundRefreshing}
            onAddTask={taskModalDisclosure.onOpen}
            onSelectTask={(task) => {
              // 使用 startTransition 包装导航操作
              startTransition(() => {
                handleSelectTask(task);
              });
            }}
            onToggleTaskDone={(task, done) => {
              // 使用 startTransition 包装可能导致 Suspense 的操作
              startTransition(() => {
                handleToggleTaskDone(task, done);
              });
            }}
            onDeleteTask={(taskId) => {
              // 使用 startTransition 包装可能导致 Suspense 的操作
              startTransition(() => {
                handleDeleteTask(taskId);
              });
            }}
          />

          {/* 任务创建模态框 */}
          <TaskCreateModal
            isOpen={taskModalDisclosure.isOpen}
            onClose={taskModalDisclosure.onClose}
            onCreateTask={async (data) => {
              const result = await handleCreateTaskWrapper(data);
              if (result) taskModalDisclosure.onClose();
              return result;
            }}
          />

          {/* 删除确认模态框 */}
          <ProjectDeleteModal
            isOpen={confirmDeleteDisclosure.isOpen}
            onClose={confirmDeleteDisclosure.onClose}
            onConfirm={(cascadeDelete) => {
              // 使用 startTransition 包装可能导致 Suspense 的操作
              startTransition(() => {
                handleDelete(cascadeDelete);
              });
            }}
            projectTitle={project.title}
            tasksCount={projectTasks.length}
          />

          {/* 分享项目模态框 */}
          <ProjectShareModal
            isOpen={shareModalDisclosure.isOpen}
            onClose={shareModalDisclosure.onClose}
            project={project}
          />
        </div>
      </div>
    );
  }

  // 加载中状态，但如果是背景刷新就不显示全屏加载
  if (loading && !isBackgroundRefreshing) {
    return (
      <div className="flex items-center justify-center h-screen w-full bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center space-y-4">
          <Spinner size="xl" thickness="4px" color="teal.500" />
          <p className="text-gray-500">{t('v0/projects:loadingText')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-4 md:pt-6 bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto py-8 px-3 md:px-6 max-w-4xl">
        <Alert status="error" mb={4} borderRadius="lg">
          <AlertIcon />
          {t('v0/projects:errors.projectNotFound')}
        </Alert>
        <Button leftIcon={<FiArrowLeft />} onClick={navigateToProjects}>
          {t('v0/projects:actions.backToList')}
        </Button>
      </div>
    </div>
  );
};